import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type AdminPageChromeCrumb = {
  label: string;
  to?: string;
};

export type AdminPageChromeState = {
  breadcrumbs?: AdminPageChromeCrumb[];
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Leading control (e.g. back) — rendered before breadcrumbs/title. */
  startAdornment?: ReactNode;
  /** Chips, primary actions — rendered before the theme shifter. */
  endAdornment?: ReactNode;
};

type AdminPageChromeContextValue = {
  chrome: AdminPageChromeState | null;
  setChrome: (next: AdminPageChromeState | null) => void;
};

const AdminPageChromeContext = createContext<AdminPageChromeContextValue | null>(null);

export function AdminPageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChromeState] = useState<AdminPageChromeState | null>(null);
  const setChrome = useCallback((next: AdminPageChromeState | null) => {
    setChromeState(next);
  }, []);
  const value = useMemo(() => ({ chrome, setChrome }), [chrome, setChrome]);
  return <AdminPageChromeContext.Provider value={value}>{children}</AdminPageChromeContext.Provider>;
}

export function useAdminPageChromeContext(): AdminPageChromeContextValue {
  const ctx = useContext(AdminPageChromeContext);
  if (!ctx) {
    throw new Error('useAdminPageChromeContext must be used within AdminPageChromeProvider');
  }
  return ctx;
}

/**
 * Registers page chrome in the Layout top bar for the lifetime of the caller.
 * Pass a memoized `chrome` object (or null) so the effect does not thrash.
 */
export function useAdminPageChrome(chrome: AdminPageChromeState | null) {
  const { setChrome } = useAdminPageChromeContext();
  useEffect(() => {
    setChrome(chrome);
    return () => setChrome(null);
  }, [setChrome, chrome]);
}
